import { formatIsoDate, MONTH_NAMES, parseIsoDate } from '../../lib/dates';

// Mirrors TimesheetPeriods on the backend: semi-monthly periods. The first half runs the 1st
// through the 15th; the second half the 16th through the last day of the month. Periods are
// variable-length and identified by their start (the 1st or the 16th). Keep in sync with
// TimesheetPeriods.

// An aligned period start is always the 1st or the 16th.
const ALIGNED_PERIOD = /^\d{4}-\d{2}-(01|16)$/;

// Whether a deep-linked periodStart is a real calendar date on the 1st or the 16th. The shape
// check alone admits impossible dates like 2026-99-01 (an API 400 instead of a page); the parse
// round-trip rejects anything the calendar re-normalizes. Callers fall back to the current
// period rather than sending junk to the server.
export function isAlignedPeriodStart(value: string): boolean {
  return ALIGNED_PERIOD.test(value) && formatIsoDate(parseIsoDate(value)) === value;
}

// The start (1st or 16th) of the period containing the given ISO date.
export function periodStartFor(iso: string): string {
  const d = parseIsoDate(iso);
  const startDom = d.getDate() <= 15 ? 1 : 16;
  return formatIsoDate(new Date(d.getFullYear(), d.getMonth(), startDom));
}

// The period start shifted by n half-months (each period is one half-month step).
export function shiftPeriod(periodStartIso: string, deltaPeriods: number): string {
  const d = parseIsoDate(periodStartIso);
  const half = d.getDate() >= 16 ? 1 : 0;
  const index = (d.getFullYear() * 12 + d.getMonth()) * 2 + half + deltaPeriods;
  const halfPart = ((index % 2) + 2) % 2;
  const monthsTotal = Math.floor(index / 2);
  const year = Math.floor(monthsTotal / 12);
  const month = ((monthsTotal % 12) + 12) % 12;
  return formatIsoDate(new Date(year, month, halfPart === 0 ? 1 : 16));
}

export function currentPeriodStart(): string {
  return periodStartFor(formatIsoDate(new Date()));
}

// The last day of the period starting at the given date (the 15th, or the month end).
function periodEndDate(start: Date): Date {
  if (start.getDate() === 1) {
    return new Date(start.getFullYear(), start.getMonth(), 15);
  }
  return new Date(start.getFullYear(), start.getMonth() + 1, 0);
}

// "1 - 15 July 2026" (periods never cross a month, so the month appears once)
export function periodLabel(periodStartIso: string): string {
  const start = parseIsoDate(periodStartIso);
  const end = periodEndDate(start);
  return `${start.getDate()} - ${end.getDate()} ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
}

// "1 - 15 July" - no year, for the compact period-stepper pill (current-year context is
// already obvious from the surrounding page; the year still shows in longer-lived lists
// like Team timesheets / History via periodLabel).
export function periodLabelShort(periodStartIso: string): string {
  const start = parseIsoDate(periodStartIso);
  const end = periodEndDate(start);
  return `${start.getDate()} - ${end.getDate()} ${MONTH_NAMES[end.getMonth()]}`;
}

// --- months (the Month status dashboard spans a month, i.e. both halves) -----------------

// "2026-07" for the month containing today.
export function currentMonth(): string {
  const now = new Date();
  return monthKey(now.getFullYear(), now.getMonth());
}

// The month key shifted by n months.
export function shiftMonth(month: string, deltaMonths: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const index = year! * 12 + (monthNumber! - 1) + deltaMonths;
  return monthKey(Math.floor(index / 12), ((index % 12) + 12) % 12);
}

// "July 2026"
export function monthLabel(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return `${MONTH_NAMES[monthNumber! - 1]} ${year}`;
}

function monthKey(year: number, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function shortDay(d: Date): string {
  return `${DOW_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]!.slice(0, 3)}`;
}

// Weekday and day-of-month on separate lines, for a compact day-column header. The month
// is omitted: a period never crosses a month and the period label already names it.
export function dayHeader(iso: string): { dow: string; date: string } {
  const d = parseIsoDate(iso);
  return {
    dow: DOW_SHORT[d.getDay()]!,
    date: String(d.getDate()),
  };
}

// "Mon 6 Jul" row label inside the grid.
export function dayLabel(iso: string): string {
  return shortDay(parseIsoDate(iso));
}
